// Modal shown when the two participants being merged have non-empty,
// different values on one or more columns. The coordinator picks which
// side's value survives, field by field. The chosen map drives a pre-
// merge UPDATE in mergeTwoParticipants that NULLs out keep's value for
// any "drop wins" field, so merge_participant_progress's COALESCE then
// pulls drop's value through.
//
// Props:
//   keep, drop:  full row objects (from detectMergeConflicts)
//   conflicts:   [{ field, keepValue, dropValue }, ...]
//   onResolve:   (resolutionMap) => void  // { fieldName: 'keep'|'drop' }
//   onCancel:    () => void
//
// The component manages its own selection state. Defaults every field
// to 'keep' (the safe, no-op default — same outcome as not opening the
// modal for that field at all).
//
// Phase C goal #3: direction-agnostic. Callers no longer need a swap
// button because every concrete data outcome is selectable here.

import { useState } from 'react'
import { fieldLabel } from '../utils/fieldLabels'
import { bahaiStatusLabel } from '../utils/bahaiStatusLabels'
import { formatPhone } from '../utils/formatPhone'

// Renders a value for display. Some fields have dedicated formatters.
const renderValue = (field, val) => {
  if (val == null || val === '') return <span className="text-stone-400 italic">empty</span>
  if (field === 'phone') return formatPhone(val)
  if (field === 'bahai_status') return bahaiStatusLabel(val)
  if (field === 'birth_date') {
    try {
      return new Date(val + 'T12:00:00').toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      })
    } catch { return String(val) }
  }
  return String(val)
}

export default function ConflictResolutionModal({ keep, drop, conflicts, onResolve, onCancel }) {
  const initial = {}
  for (const c of conflicts) initial[c.field] = 'keep'
  const [picks, setPicks] = useState(initial)

  const setAll = (side) => {
    const next = {}
    for (const c of conflicts) next[c.field] = side
    setPicks(next)
  }

  const keepName = `${keep.first_name || ''} ${keep.last_name || ''}`.trim() || 'Keep record'
  const dropName = `${drop.first_name || ''} ${drop.last_name || ''}`.trim() || 'Duplicate record'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-xl p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-800">Resolve conflicts</h2>
          <button onClick={onCancel} className="text-gray-400 text-2xl leading-none">×</button>
        </div>
        <p className="text-sm text-stone-600 mb-4">
          Both records have data for these fields. Pick which value should survive on the merged record.
        </p>

        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => setAll('keep')}
            className="flex-1 text-xs font-semibold text-teal-700 border border-teal-200 rounded-full px-3 py-1.5 hover:bg-teal-50">
            Use all from {keepName || 'left'}
          </button>
          <button
            type="button"
            onClick={() => setAll('drop')}
            className="flex-1 text-xs font-semibold text-stone-600 border border-stone-200 rounded-full px-3 py-1.5 hover:bg-stone-50">
            Use all from {dropName || 'right'}
          </button>
        </div>

        <div className="space-y-3 mb-5">
          {conflicts.map(c => (
            <div key={c.field} className="border border-stone-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                {fieldLabel(c.field)}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPicks(p => ({ ...p, [c.field]: 'keep' }))}
                  className={`text-left rounded-lg px-3 py-2 border-2 transition-colors ${
                    picks[c.field] === 'keep'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-stone-200 bg-white hover:bg-stone-50'
                  }`}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-teal-600 mb-0.5">Keep</p>
                  <p className="text-sm text-gray-800 break-words">{renderValue(c.field, c.keepValue)}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPicks(p => ({ ...p, [c.field]: 'drop' }))}
                  className={`text-left rounded-lg px-3 py-2 border-2 transition-colors ${
                    picks[c.field] === 'drop'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-stone-200 bg-white hover:bg-stone-50'
                  }`}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500 mb-0.5">Duplicate</p>
                  <p className="text-sm text-gray-800 break-words">{renderValue(c.field, c.dropValue)}</p>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-stone-100 text-stone-700 font-semibold py-3 rounded-xl">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onResolve(picks)}
            className="flex-1 bg-teal-600 text-white font-semibold py-3 rounded-xl hover:bg-teal-700">
            Merge
          </button>
        </div>
      </div>
    </div>
  )
}
